import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Shield, PackageCheck, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { superAdminApi } from '../lib/api';
import type { SuperAdminCollectorOverview } from '../lib/api';
import { PageHeader } from '../components/ui/PageHeader';

const runtimeStateLabels: Record<string, string> = {
  NOT_REGISTERED: 'Nie zarejestrowany',
  DISABLED: 'Wstrzymany',
  IDLE: 'Bezczynny',
  RUNNING: 'Pracuje',
  DEGRADED: 'Ograniczony',
  STOPPED: 'Zatrzymany',
};
const runtimeOutcomeLabels: Record<string, string> = {
  NONE: 'Brak',
  DISABLED: 'Wstrzymany przełącznikiem',
  OK: 'OK',
  DEGRADED: 'Z ostrzeżeniami',
  STOPPED: 'Zatrzymany',
};

export function SuperAdminFinanceCollectorPage() {
  const { user } = useAuthStore();

  if (user?.role !== 'SUPER_ADMIN') return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        icon={Shield}
        title="Pozyskiwanie dowodów finansowych"
        subtitle="Globalny przełącznik kolektora dowodów dla wszystkich firm na platformie"
      />
      <SuperAdminFinanceCollectorContent />
    </div>
  );
}

export function SuperAdminFinanceCollectorContent() {
  const [overview, setOverview] = useState<SuperAdminCollectorOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const load = async () => {
    try {
      const { data } = await superAdminApi.getFinanceCollector();
      setOverview(data);
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się odczytać stanu kolektora' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleToggle = async (enabled: boolean) => {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await superAdminApi.updateFinanceCollector(enabled);
      setOverview(data);
      setFeedback({ type: 'success', message: enabled ? 'Włączono pozyskiwanie dowodów globalnie' : 'Wstrzymano pozyskiwanie dowodów globalnie' });
    } catch {
      setFeedback({ type: 'error', message: 'Nie udało się zapisać przełącznika' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {feedback && (
        <div className={`flex items-center gap-2 p-3 rounded-xl text-sm ${
          feedback.type === 'success'
            ? 'bg-success/10 border border-success/30 text-success'
            : 'bg-danger/10 border border-danger/30 text-danger'
        }`}>
          {feedback.type === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
          {feedback.message}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
        </div>
      ) : overview && (
        <div className="bg-surface-raised/50 border border-border-subtle rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-3">
            <PackageCheck className="w-5 h-5 text-accent-primary" />
            <h3 className="text-base font-semibold text-text-primary">Kolektor dowodów (master switch)</h3>
          </div>

          <p className="text-sm text-text-secondary">
            Kolektor zapisuje u klientów niezmienne kopie dowodów finansowych (zamówienia, przesyłki, koszty).
            Ten przełącznik wstrzymuje lub wznawia pozyskiwanie na całej platformie i jest widziany
            przez proces roboczy w ciągu około 1 minuty. Wyłączenie nie usuwa zachowanych dowodów.
          </p>

          <label className="flex items-center gap-3 p-3 bg-surface-hover/30 rounded-xl cursor-pointer hover:bg-surface-hover/50 transition-colors">
            <input
              type="checkbox"
              checked={overview.enabled}
              disabled={saving}
              onChange={(e) => handleToggle(e.target.checked)}
              className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
            />
            <div>
              <span className="text-text-primary text-sm font-medium">
                Pozyskiwanie dowodów {overview.enabled ? 'włączone' : 'wstrzymane'}
              </span>
              <p className="text-xs text-text-muted mt-0.5">
                To jest globalny kill switch. Klienci włączają sklepy samodzielnie w ustawieniach Finansów,
                ale przy wstrzymaniu globalnym nic nie jest pobierane.
              </p>
            </div>
            {saving && <Loader2 className="w-4 h-4 animate-spin text-accent-primary ml-auto" />}
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-3 bg-surface-hover/30 rounded-xl">
              <p className="text-xs text-text-muted">Stan procesu roboczego</p>
              <p className="text-sm text-text-primary font-medium mt-1">
                {overview.runtime ? runtimeStateLabels[overview.runtime.state] ?? overview.runtime.state : 'Brak danych (worker offline)'}
              </p>
            </div>
            <div className="p-3 bg-surface-hover/30 rounded-xl">
              <p className="text-xs text-text-muted">Ostatni cykl</p>
              <p className="text-sm text-text-primary font-medium mt-1">
                {overview.runtime?.lastTickAt ? new Date(overview.runtime.lastTickAt).toLocaleString('pl-PL') : 'Brak'}
              </p>
            </div>
            <div className="p-3 bg-surface-hover/30 rounded-xl">
              <p className="text-xs text-text-muted">Wynik ostatniego cyklu</p>
              <p className="text-sm text-text-primary font-medium mt-1">
                {overview.runtime ? runtimeOutcomeLabels[overview.runtime.lastOutcome] ?? overview.runtime.lastOutcome : 'Brak danych'}
              </p>
            </div>
          </div>

          <div className="bg-accent-primary/5 border border-accent-primary/20 rounded-xl p-4 space-y-1">
            <p className="text-xs text-text-secondary">
              Cykl co {Math.round(overview.policy.intervalMs / 60000)} minut.
              Co najwyżej {overview.policy.tenantItemsPerDay} rekordów dziennie na firmę
              i {overview.policy.globalItemsPerDay} rekordów dziennie na całą platformę.
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => void load()}
              className="btn-secondary text-xs flex items-center gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Odśwież stan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
